import React, { useEffect, useState, useCallback } from "react";
import { Briefcase, ArrowRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

/**
 * EngagementPromptsSection — REX v3 Phase 2 morning prompt loop.
 *
 * Pulls /v2/workspaces/{ws}/engagements/prompts and renders each as a
 * lightweight action card. Tapping a prompt deep-links into the Engagements
 * surface focused on that record.
 */
export default function EngagementPromptsSection() {
  const [workspaceId, setWorkspaceId] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async (wsId) => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/engagements/prompts`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setPrompts(d.prompts || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    import("@/lib/workspaceFetch").then(({ fetchMyWorkspaces }) =>
      fetchMyWorkspaces()
        .then((rows) => {
          if (rows?.[0]) {
            setWorkspaceId(rows[0].workspace_id);
            load(rows[0].workspace_id);
          } else {
            setLoading(false);
          }
        })
        .catch(() => setLoading(false))
    );
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 py-2" data-testid="engagement-prompts-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking engagement prompts…
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <p className="text-sm text-slate-400 py-2" data-testid="engagement-prompts-empty">
        No engagement prompts today — all active engagements look healthy.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="engagement-prompts-list">
      {prompts.map((p, i) => (
        <button
          key={`${p.kind}-${p.engagement_id}-${i}`}
          onClick={() => navigate(`/engagements?focus=${p.engagement_id}`)}
          className="w-full text-left flex items-start gap-3 border border-slate-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50/40 transition-colors"
          data-testid={`engagement-prompt-${p.kind}-${i}`}
        >
          <Briefcase className="h-4 w-4 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-800">{p.headline}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {p.client_name && <span className="mr-2">· {p.client_name}</span>}
              <span className="uppercase tracking-wide">{p.kind.replace("_", " ")}</span>
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400 mt-1" />
        </button>
      ))}
    </div>
  );
}
